/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {Frequency} from "@app/app-shared/care_config";
import {BadImplementationException, currentTimeMillies, Day} from "@intuitionrobotics/ts-common";
import dayjs from "../../../utils/dayjs";

export enum Period {
    AM = "AM",
    PM = "PM",
}

export class DateTimesUtil {

    private static BIRTHDAY_PRESENTATION_FORMAT: string = "MMMM D, YYYY";
    private static BIRTHDAY_PRESENTATION_FORMAT_WO_YEAR: string = "MMMM D";

    private static TIME_FORMAT: string = "h:mm A [(]z[)]";
    private static TIMEZONE_FORMAT: string = "[(]z[)]";

    private static APPOINTMENT_PRESENTATION_FORMAT: string = "MM/DD/YYYY";

    public static frequencyPresentation = (frequency: Frequency): string => {
        if (!frequency)
            return 'Missing value';

        const times = frequency.times;
        switch (frequency.value) {
            case "day":
                if (times === 1)
                    return `Daily`;
                if (times === 0.5)
                    return `Every other day`;
                return `${times} times a day`;
            case "week":
                if (times === 1)
                    return 'Weekly';
                return `${times} times a week`;
            case "month":
                if (times === 1)
                    return `Monthly`;
                return `${times} times a month`;
            default:
                throw new BadImplementationException('you must have a value of day, week, or month');
        }
    };

    public static alertsPresentation = (timestamp: number) => {
        const todayAtMidnight = dayjs().startOf('day').valueOf();
        const time = dayjs(timestamp);
        const dayInMillis = 86400000;
        // const timePretty = `${time.hours()}:${String(time.minutes()).padStart(2, "0")}`;
        const timePretty = dayjs(timestamp).format("hh:mm A");
        const monthsArray = ['Jan',
            'Feb',
            'March',
            'April',
            'May',
            'June',
            'July',
            'Aug',
            'Sept',
            'Oct',
            'Nov',
            'Dec'];
        if (todayAtMidnight === time.startOf('day').valueOf())
            return `Today, ${timePretty}`;
        if (todayAtMidnight - dayInMillis === time.startOf('day').valueOf())
            return `Yesterday, ${timePretty}`;
        return `${monthsArray[time.month()]} ${time.date()}`;
    };

    public static birthdayPresentation = (timestamp: number): string => {
        const birthdayMoment = dayjs.utc(timestamp);
        if (birthdayMoment.isBefore(currentTimeMillies() - 10 * Day * 365)) // before 10 years, show the year
            return birthdayMoment.format(DateTimesUtil.BIRTHDAY_PRESENTATION_FORMAT);

        return birthdayMoment.format(DateTimesUtil.BIRTHDAY_PRESENTATION_FORMAT_WO_YEAR);
    };

    public static timePresentation = (tz: string): string => {
        return dayjs().tz(tz).format(DateTimesUtil.TIME_FORMAT);
    };

    public static appointmentPresentation = (timestamp: number, tz: string): string => {
        return dayjs.utc(timestamp).tz(tz).format(DateTimesUtil.APPOINTMENT_PRESENTATION_FORMAT);
    };

    public static appointmentSetPresentation = (timestamp: number, tz: string): string => {
        const date = dayjs(timestamp);
        return `${date.format("YYYY-MM-DD h:mm A")} ${date.tz(tz).format(DateTimesUtil.TIMEZONE_FORMAT)}`;
    };
}
